import { initRpc } from 'src/services/rpc/initRpc';
import type { DetachReleaseResponse } from 'src/services/rpc/detach/scheme';
import {
  call,
  delay,
  race,
  select,
  spawn,
  take,
  takeLatest,
} from 'redux-saga/effects';
import { DetachRpc, Rpc } from '@dynamika/rpc-core/contracts';
import { viewerRpcScheme as viewerRpc } from 'src/services/rpc/viewer/scheme';
import { isRpcTimeoutError, tellViewer } from 'src/services/rpc/helpers';
import {
  detachRpcScheme,
  type DetachInitializeResponse,
} from 'src/services/rpc';
import { actions } from '@restore/readerViewerSession/tabCoordination/tabCoordination.slice';
import { dispatch } from '@restore/helpers';
import { openDetachedWindow } from 'src/utils/openDetachedWindow';
import {
  selectTabCoordinationState,
  selectScoringFormCode,
  selectFormManagementState,
  selectReadingTaskState,
  selectReaderTaskState,
  selectCurrentSubTaskState,
} from '@restore/readerViewerSession/utils';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  scoringFormUpdateAction,
  scoringFormFetchTaskDoneAction,
  scoringFormSeriesChangedAction,
} from 'src/pages/Readings/ReadingTaskPage/utils';
import { ReaderTaskSaveOrCompleteSuccessAction } from '@restore/readerTask/readerTask.types';
import { prepareUrl } from 'src/utils/routerHelpers';
import { mapUserRoleToTaskEndpoint } from 'src/store-redux/readerTaskOrchestration/workers/viewerModeMapper';
import { UserRoles } from '@dynamika-shared/types/auth';
import { routesPath } from 'src/router/routes';
import { goToRoute } from '@dynamika-shared/utils/sagaRouter';
import { readerTaskSlice } from '@restore/readerTask/readerTask.slice';
import { readerSubTaskSlice } from '@restore/readerSubTask/readerSubTask.slice';
import { CloseTabAction } from '@restore/readerViewerSession/tabCoordination/tabCoordination.types';
import { formManagementSlice } from '@restore/readerViewerSession/formManagement/formManagement.slice';
import { readerViewerPageSelector } from '@restore/readerTaskOrchestration/readerTaskOrchestration.slice';
import { contextChangedWorker } from './workers';
import {
  getDetachedWindow,
  isDetachedWindowClosed,
  readDetachedClosePayload,
  rememberDetachedWindow,
} from './detachedWindow';

const TIME_TO_STOP_LOADING = 300;
const TIME_TO_REVIVE_FORM = 2000;
const DETACH_INITIALIZE_TIMEOUT_MS = 10000;
const TIME_TO_INITIALIZE = 30000;
const DETACHED_WINDOW_POLL_MS = 500;
const DETACHED_PEER_PROBE_MS = 1500;

export const resolveReattachTaskIdentity = (
  orchestration: { taskId?: string; clinicalTrialId?: string },
  currentTask?: { id?: string; clinicalTrialId?: string } | null
) => ({
  taskId: orchestration.taskId || currentTask?.id || undefined,
  clinicalTrialId:
    orchestration.clinicalTrialId || currentTask?.clinicalTrialId || undefined,
});

const {
  saveReaderTaskSuccess,
  saveReaderTaskFailure,
  completeReaderTaskSuccess,
  completeReaderTaskFailure,
} = readerTaskSlice.actions;

const {
  setLoading,
  setIsDetached,
  deAttach,
  spawnConnect,
  setIsDeactivated,
  setInitialized,
  setMainTab,
  setMainWindowName,
  storageTabClosed,
} = actions;

// Mirrors `mainTab` flag into `mainWindowName` so addressed peer requests
// (`${role}-${mainWindowName}`) work in attached main tab as well, not only in
// spawn (where the session is encoded in the window name).
export function* setMainTabSyncWorker({ payload }: PayloadAction<boolean>) {
  yield dispatch(setMainWindowName(payload ? window.name : null));
}

/**
 * Ask the main tab for everything this window needs to carry on
 *
 * Plain terms
 * -----------
 * The window the reader just popped out starts empty. Before it can show the
 * form it has to ask the window it came from for what the reader had in front
 * of them: the values typed so far, whether they were unsaved, and which series
 * is on screen. If that window does not answer in time, there is nothing to
 * show and the detached window says so instead of hanging.
 *
 * Technical
 * ---------
 * A single addressed request to `d7-${mainWindowName}` with an explicit
 * `timeoutMs`. The deadline belongs to the request, not to a `race` against a
 * `delay`: the peer rejects the pending call and frees its entry, whereas a
 * losing `race` branch leaves the call pending until the engine default (15s)
 * expires. Returns `null` on any failure — the caller deactivates the tab.
 */
function* initializeDetachedTab(mainWindowName: string) {
  try {
    const response: DetachInitializeResponse = yield call(
      detachRpcScheme.requests.initialize,
      mainWindowName,
      DETACH_INITIALIZE_TIMEOUT_MS
    );

    return response;
  } catch (error) {
    console.error('[rpc] detach initialization failed', error);

    return null;
  }
}

/**
 *  start connection process and create new tab with scoring form
 *
 */
export function* runDeattachMainTabProcessWorker() {
  const { isDetached, reattachInProgress } = yield* selectTabCoordinationState();

  if (isDetached || reattachInProgress) return;

  // Detach flow should not reset dirty on next render
  yield dispatch(formManagementSlice.actions.setResetDirtyOnNextRender(false));

  const detachedWindow: Window | null = yield call(openDetachedWindow);

  // The browser blocked the popup — nothing to announce.
  if (!detachedWindow) {
    return;
  }

  // Raising this flag is what removes the form from this window: the page
  // renders it only while `!isDetached`. The detached window still has
  // a whole document load ahead of it before it can claim the shared address.
  yield dispatch(setIsDetached(true));

  yield call(rememberDetachedWindow, detachedWindow);

  // `spawn`, not `fork`: `takeLatest(deAttach)` cancels this worker together
  // with its attached forks on the next click, while the window stays open.
  yield spawn(watchDetachedWindowWorker, detachedWindow);

  yield* tellViewer(
    viewerRpc.requests.study.setDetached,
    { isDetached: true },
    'detach'
  );
}

export function* runDeattachSpawnTabProcessWorker() {
  yield call(initRpc);

  if (!window.name) return;

  // `mainWindowName` starts null, so the form stays unmounted until the
  // `initialize` reply supplies the session identity.
  yield dispatch(setMainWindowName(null));
  yield dispatch(actions.setInitializationError(null));
  yield dispatch(setIsDeactivated(false));
  yield dispatch(setLoading(true));
  yield dispatch(setInitialized(false));
  // Detach/spawn side should not reset dirty on next render
  yield dispatch(formManagementSlice.actions.setResetDirtyOnNextRender(false));
  yield dispatch(setIsDetached(true));

  yield delay(TIME_TO_STOP_LOADING);

  // Without the opener's identity there is nobody to ask for the session state.
  const mainWindowName = window.name;

  if (!mainWindowName) {
    yield dispatch(actions.setInitializationError('The main session identity is missing.'));
    yield dispatch(setLoading(false));
    yield dispatch(setIsDeactivated(true));
    return;
  }

  const initialization = yield* initializeDetachedTab(mainWindowName);

  yield dispatch(setLoading(false));

  if (!initialization) {
    yield dispatch(actions.setInitializationError('The main window did not provide the form state. Retry initialization.'));
    yield dispatch(setIsDeactivated(true));
    return;
  }

  // Unpack the initialize response BEFORE awaiting scoringFormRenderHook. The form
  // gates its own render on `mainWindowName !== null` (peer addressing relies
  // on it), so applying mainWindowName first is what unblocks the form to
  // emit the render hook this worker is waiting for. Inverting the order
  // deadlocks the spawn flow.
  const {
    formData,
    isDirty,
    lastSeries,
    mainWindowName: initializedMainWindowName,
  } = initialization;


  if (initializedMainWindowName) {
    yield dispatch(setMainWindowName(initializedMainWindowName));
  }

  const { formRendered } = yield* selectFormManagementState();

  if (!formRendered) {
    const { initializeTimeout } = yield race({
      formRendered: take(
        formManagementSlice.actions.scoringFormRenderHook.type
      ),
      initializeTimeout: delay(TIME_TO_INITIALIZE),
    });

    if (initializeTimeout) {
      yield dispatch(actions.setInitializationError('The form did not finish loading. Retry initialization.'));
      yield dispatch(setIsDeactivated(true));
      return;
    }
  }

  const scoringCode = yield* selectScoringFormCode();
  if (formData) {
    yield call(scoringFormUpdateAction, scoringCode, {
      formData,
    });
  }
  if (typeof isDirty === 'boolean') {
    yield dispatch(formManagementSlice.actions.setFormDirty(isDirty));
  }
  if (Array.isArray(lastSeries) && lastSeries.length > 0) {
    yield call(scoringFormSeriesChangedAction, scoringCode, lastSeries);
  }
  // Hide loader only after applying the initial state.
  yield dispatch(setInitialized(true));
}

export function* saveOrCompleteHandleWorker({
  payload,
}: ReaderTaskSaveOrCompleteSuccessAction) {
  const { mainTab } = yield* selectTabCoordinationState();
  if (!mainTab) {
    return;
  }
  const task = yield* selectReadingTaskState();

  if (payload?.shouldRedirect) {
    // Only notify detached tabs to deactivate when we are completing and redirecting
    yield call(detachRpcScheme.events.deactivate, {});

    const userRole: UserRoles | null = yield select(
      (state: { auth?: { data?: { selectedRole?: UserRoles } } }) =>
        state?.auth?.data?.selectedRole || null
    );

    const redirectRoute =
      userRole === UserRoles.EXTERNAL_USER
        ? routesPath.externalUserSubjectsClinicalTrial
        : routesPath.readerClinicalTrial;

    yield goToRoute(prepareUrl(redirectRoute, [task?.clinicalTrialId ?? '']));
  } else {
    // No redirect: ensure main tab refreshes task so UI reflects completed/readOnly immediately
    const taskId = task?.id as string | undefined;
    const clinicalTrialId = task?.clinicalTrialId as string | undefined;

    const userRole: UserRoles | null = yield select(
      (state: { auth?: { data?: { selectedRole?: UserRoles } } }) =>
        state?.auth?.data?.selectedRole || null
    );

    if (taskId && clinicalTrialId) {
      yield dispatch(
        readerTaskSlice.actions.fetchReaderTask({
          clinicalTrialId,
          taskId,
          shouldRefetch: true,
          endpoint: mapUserRoleToTaskEndpoint(userRole),
        })
      );
    }
  }
}

export function* saveOrCompleteFailureHandleWorker(
  action: PayloadAction<unknown>
) {
  const { mainTab } = yield* selectTabCoordinationState();
  if (mainTab) {
    yield call(detachRpcScheme.events.operationFailed, {
      name: action.type === saveReaderTaskFailure.type ? 'save' : 'complete',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      message: (action.payload as any)?.response?.data?.detail,
    });
  }
}

export function* closeViewerWorker() {
  const { mainTab } = yield* selectTabCoordinationState();
  if (mainTab) {
    yield call(detachRpcScheme.events.deactivate, {});
  }
}

export function* disconnectWorker() {
  const { formDataCache } = yield* selectFormManagementState();
  // Preserve unsaved state restored from the detached snapshot.
  yield dispatch(formManagementSlice.actions.setResetDirtyOnNextRender(false));
  yield dispatch(setIsDetached(false));
  // Inform viewer that it is no longer in detached mode
  yield* tellViewer(
    viewerRpc.requests.study.setDetached,
    { isDetached: false },
    'reattach'
  );
  window.D7ScoringFormRendered = true;

  // NEW: Refetch current reader task on reattach
  const currentTask = yield* selectReadingTaskState();
  const orchestrationState: ReturnType<typeof readerViewerPageSelector> =
    yield select(readerViewerPageSelector);
  // readerTask.data can temporarily contain a prefetched child from older
  // sessions/builds. The orchestration slice is keyed from the route and keeps
  // the real parent id stable across detached-tab reattachment.
  const { taskId, clinicalTrialId } = resolveReattachTaskIdentity(
    orchestrationState,
    currentTask
  );

  const userRole: UserRoles | null = yield select(
    (state: { auth?: { data?: { selectedRole?: UserRoles } } }) =>
      state?.auth?.data?.selectedRole || null
  );

  if (taskId && clinicalTrialId) {
    yield dispatch(
      readerTaskSlice.actions.fetchReaderTask({
        clinicalTrialId,
        taskId,
        shouldRefetch: true,
        endpoint: mapUserRoleToTaskEndpoint(userRole),
      })
    );
  }

  // Also refetch subtask if present
  const currentSubTask = yield* selectCurrentSubTaskState();
  const subTaskId = (currentSubTask?.id as string) || undefined;
  const subTaskClinicalTrialId =
    (currentSubTask?.clinicalTrialId as string) || clinicalTrialId;
  if (subTaskId && subTaskClinicalTrialId) {
    yield dispatch(
      readerSubTaskSlice.actions.fetchReaderSubTask({
        clinicalTrialId: subTaskClinicalTrialId,
        taskId,
        subTaskId,
        endpoint: mapUserRoleToTaskEndpoint(userRole, true),
      })
    );
  }

  // Give UI a moment to settle after re-attach, then restore cached scoring data locally
  yield delay(TIME_TO_REVIVE_FORM);
  const scoringFormCode = yield* selectScoringFormCode();
  if (formDataCache) {
    yield call(scoringFormUpdateAction, scoringFormCode, {
      formData: formDataCache,
    });
    // After reattaching and restoring cached data, notify the scoring form to reinitialize its state
    yield call(scoringFormFetchTaskDoneAction, scoringFormCode, {});
  } else {
    // Even without cached data, ask the scoring form to reinitialize to ensure measurements appear
    yield call(scoringFormFetchTaskDoneAction, scoringFormCode, {});
  }
  const { lastSeries } = yield* selectFormManagementState();
  if (Array.isArray(lastSeries) && lastSeries.length > 0) {
    yield call(scoringFormSeriesChangedAction, scoringFormCode, lastSeries);
  }
}

export function* storageTabClosedWorker({
  payload,
}: PayloadAction<CloseTabAction['payload']>) {
  const { mainTab, isDetached, reattachInProgress } = yield* selectTabCoordinationState();

  if (mainTab && payload.role === 'spawn') {
    if (!isDetached || reattachInProgress) return;
    /**
     * Spawn tab closed right after submitting — nothing to attach back
     *
     * Plain terms
     * -----------
     * Closing the detached window normally means "put the form back into the
     * main window". After the report has been signed there is nothing to put
     * back: the main tab is already on its way to the list of readings, and the
     * only form data it still holds is the snapshot from before the detach —
     * restoring it would make a submitted reading look unsubmitted.
     *
     * Technical
     * ---------
     * The re-attach below (`disconnectWorker`) both
     * re-mounts the form and pushes the stale `formDataCache` into it 2s later.
     * `leaveReadingAfterSubmitWorker` (contextChangedWorker) owns this case
     * instead, including clearing `isDetached` once the tab has left.
     */
    if (payload.reason === 'AFTER_SUBMIT_REDIRECT') {
      return;
    }

    const readerTaskState = yield* selectReaderTaskState();
    const taskId = readerTaskState?.data?.id as unknown as string;
    const { formData, isValid } = (payload?.[taskId] ?? {}) as {
      formData: object;
      isValid: boolean;
    };
    if (formData) {
      const scoringFormCode = yield* selectScoringFormCode();
      // Update scoring form UI
      yield call(scoringFormUpdateAction, scoringFormCode, { formData });
      // Also update the cached form data inside formManagement to keep LS/cache in sync
      yield dispatch(
        formManagementSlice.actions.changeScoringFormData({
          formData,
          isValid,
        })
      );
    }
    // Manual departure is an alternative to the explicit release RPC reply.
    yield dispatch(actions.setReattachInProgress(true));

    try {
      yield call(disconnectWorker);
    } finally {
      yield dispatch(actions.setReattachInProgress(false));
    }
    return;
  }
  if (!mainTab && payload.role === 'main') {
    // Main closed -> spawn should deactivate and drop the detach session, then redirect to readings list
    yield dispatch(actions.setIsDeactivated(true));

    // Redirect to list of readings for the current clinical trial
    const readerTaskState = yield* selectReaderTaskState();
    const clinicalTrialId =
      (readerTaskState?.data?.clinicalTrialId as string) || '';
    if (clinicalTrialId) {
      const userRole: UserRoles | null = yield select(
        (state: { auth?: { data?: { selectedRole?: UserRoles } } }) =>
          state?.auth?.data?.selectedRole || null
      );

      const redirectRoute =
        userRole === UserRoles.EXTERNAL_USER
          ? routesPath.externalUserSubjectsClinicalTrial
          : routesPath.readerClinicalTrialReadings;

      const listUrl = prepareUrl(redirectRoute, [clinicalTrialId]);
      yield goToRoute(listUrl);
    } else {
      // Fallback: go to reader home if no CT id available
      yield goToRoute(routesPath.reader);
    }
  }
}

/** Restore only after the old form released its peer or its host left. */
export const isDetachedCloseAction = (action: { type: string; payload?: CloseTabAction['payload'] }) =>
  action.type === storageTabClosed.type &&
  action.payload?.role === 'spawn' &&
  action.payload?.tabId === window.name;

/** `false` only when nobody answered in time — any reply means a live peer. */
export function* isDetachedPeerAnswering() {
  try {
    yield call(detachRpcScheme.requests.probe, DETACHED_PEER_PROBE_MS);

    return true;
  } catch (error) {
    return !isRpcTimeoutError(error);
  }
}

/** Puts the form back as if the closed window had announced its departure. */
function* reattachFromClosedWindow() {
  const payload: CloseTabAction['payload'] = yield call(
    readDetachedClosePayload
  );

  yield dispatch(actions.setReattachInProgress(false));

  yield call(storageTabClosedWorker, storageTabClosed(payload));
}


export function* watchDetachedWindowWorker(detachedWindow: Window) {
  while (true) {
    yield delay(DETACHED_WINDOW_POLL_MS);

    const { isDetached } = yield* selectTabCoordinationState();

    if (!isDetached || getDetachedWindow() !== detachedWindow) return;

    if (detachedWindow.closed) {
      const answering: boolean = yield call(isDetachedPeerAnswering);

      if (!answering) break;
    }
  }

  const { isDetached, reattachInProgress } =
    yield* selectTabCoordinationState();

  if (!isDetached || getDetachedWindow() !== detachedWindow) return;

  if (reattachInProgress) {
    const payload: CloseTabAction['payload'] = yield call(
      readDetachedClosePayload
    );

    yield dispatch(actions.detachedWindowGone(payload));

    return;
  }

  yield* reattachFromClosedWindow();
}


export function* requestReattachWorker() {
  const tab = yield* selectTabCoordinationState();

  if (!tab.mainTab || !tab.isDetached) return;

  yield dispatch(actions.setReattachInProgress(true));

  try {
    const result: {
      released?: DetachReleaseResponse;
      closed?: CloseTabAction;
      gone?: CloseTabAction;
    } = yield race({
      released: call(detachRpcScheme.requests.releaseForm, 10000),
      closed: take(isDetachedCloseAction),
      gone: take(actions.detachedWindowGone.type),
    });

    const departed = result.closed ?? result.gone;

    if (departed) {
      yield dispatch(actions.setReattachInProgress(false));
      yield call(storageTabClosedWorker, departed);
      return;
    }

    const released = result.released;

    if (!released || released.mainWindowName !== window.name) {
      throw new Error('The detached reply belongs to another session');
    }

    if (released.formData) {
      yield dispatch(formManagementSlice.actions.changeScoringFormData({
        formData: released.formData,
        isValid: released.isValid ?? false,
      }));
    }

    yield dispatch(formManagementSlice.actions.setFormDirty(released.isDirty));
    yield dispatch(formManagementSlice.actions.setLastSeries(released.lastSeries));

    yield call(detachRpcScheme.events.completeAttach, {});

    yield call(disconnectWorker);
  } catch (error) {
    const windowClosed: boolean = yield call(isDetachedWindowClosed);

    if (windowClosed && isRpcTimeoutError(error)) {
      yield* reattachFromClosedWindow();

      return;
    }

    console.error('[rpc] form return was not confirmed', error);
    alert('The detached form has not confirmed it stopped. Retry Attach, or close the detached window.');
  } finally {
    yield dispatch(actions.setReattachInProgress(false));
  }
}

export function* tabCoordinationSaga() {
  yield takeLatest(setMainTab.type, setMainTabSyncWorker);
  yield takeLatest(deAttach.type, runDeattachMainTabProcessWorker);
  yield takeLatest(spawnConnect.type, runDeattachSpawnTabProcessWorker);

  yield takeLatest(storageTabClosed.type, storageTabClosedWorker);
  yield takeLatest(DetachRpc.Events.CONTEXT_CHANGED, contextChangedWorker);

  yield takeLatest(
    [saveReaderTaskSuccess.type, completeReaderTaskSuccess.type],
    saveOrCompleteHandleWorker
  );

  yield takeLatest(
    [saveReaderTaskFailure.type, completeReaderTaskFailure.type],
    saveOrCompleteFailureHandleWorker
  );

  yield takeLatest(Rpc.Events.Viewer.CLOSED, closeViewerWorker);
}
